import React, { useState } from 'react';
import { useCopyToClipboard } from 'usehooks-ts';

import { useAccountContext } from '@/lib/accounts/context';
import { Button, IconButton } from '@/components/button';
import { Input } from '@/components/form/input';
import { useCreateGoogleDriveIntegrationMutation } from '@/graphql';
import { showChatWidget } from '@/lib/utils/support';

import { Tooltip } from '@/components/tooltip';

import formStyles from '@/components/form/form.module.scss';
import styles from '../integrations.module.scss';

interface GoogleDriveFormProps {
  onSuccess(): void;
  onClose(): void;
}

const ServiceAccountEmail = 'google-drive-reader@supersimple-production.iam.gserviceaccount.com';

export const GoogleDriveForm = ({ onSuccess, onClose }: GoogleDriveFormProps) => {
  const { account } = useAccountContext();
  const [createGoogleDriveIntegration, { loading }] = useCreateGoogleDriveIntegrationMutation();
  const [, copyToClipboard] = useCopyToClipboard();
  const [showGoogleDriveError, setShowGoogleDriveError] = useState(false);
  const [folderUrl, setFolderUrl] = useState('');
  const [emailCopied, setEmailCopied] = useState(false);

  const handleAddGoogleDrive = async (event: React.FormEvent) => {
    event.preventDefault();
    const { data } = await createGoogleDriveIntegration({
      variables: { url: folderUrl, accountId: account.accountId },
      refetchQueries: ['AccountSettings'],
    });
    const success = data?.createGoogleDriveIntegration.success;

    if (success === false) {
      return setShowGoogleDriveError(true);
    }

    setFolderUrl('');
    setShowGoogleDriveError(false);
    onSuccess();
  };

  const handleCopy = async () => {
    await copyToClipboard(ServiceAccountEmail);
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  };

  return (
    <>
      <section className={styles.integrationModal}>
        <h2>Integrate Google Drive</h2>
        <p>
          To integrate Google Drive, share the relevant folders or Drives with Supersimple&apos;s
          email.
          <br /> Set the permission level to Viewer (read-only access).
        </p>
        <div>
          <p>Use this email for sharing your folders:</p>
          <div className={styles.integrationCopyWrapper}>
            <code onClick={handleCopy}>{ServiceAccountEmail}</code>

            <Tooltip content={emailCopied ? 'Copied!' : 'Copy email'}>
              <IconButton
                icon="Copy"
                onClick={handleCopy}
                onMouseLeave={() => setTimeout(() => setEmailCopied(false), 500)}
              />
            </Tooltip>
          </div>
        </div>
        <p>
          More details in our&nbsp;
          <a
            href="https://docs.supersimple.io/integrations/google-drive"
            target="_blank"
            rel="noopener noreferrer">
            integration guide
          </a>
          .
          <br />
        </p>
      </section>
      <form className={styles.integrationForm} onSubmit={handleAddGoogleDrive}>
        {showGoogleDriveError && (
          <>
            <p className={styles.integrationError}>
              We couldn&apos;t access your Google Drive. Please verify you&apos;ve given Viewer
              access to our email.
            </p>
            <p>
              If you need further assistance, you can always{' '}
              <a href="#" onClick={showChatWidget}>
                chat with us
              </a>
              .
            </p>
          </>
        )}

        <Input
          value={folderUrl}
          placeholder="Google Drive URL"
          name="folder-url"
          type="text"
          size="regular"
          autoComplete="off"
          autoFocus
          onChange={(e) => setFolderUrl(e.currentTarget.value ?? '')}
        />

        <footer className={formStyles.formControls}>
          <Button type="submit" loading={loading} disabled={loading || !folderUrl.length}>
            Add Google Drive
          </Button>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
        </footer>
      </form>
    </>
  );
};
